import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import hyperid from 'hyperid';
import debounce from 'lodash/debounce';
import { createContext, type FC, type PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useStopwatch } from 'react-use-precision-timer';

import { AbstractDeviceRegistration } from './AbstractDeviceRegistration';

import {
	type BuildDeviceIntelligenceResult,
	type DeviceVendor,
	Journey,
	PhoneCallStatus,
	type VendorClientInput,
} from '../../.generated/types';
import { buildDeviceIntelligence } from '../server/actions/buildDeviceIntelligence';
import { determineApolloClient, determineAuthToken } from '../server/utils/utilities';

export interface DeviceIntelligenceProviderProps extends PropsWithChildren {
	journey: Journey;
	onRegistrationComplete?: (registrationId: string, vendors: DeviceVendor[]) => void;
	onCollectionComplete?: (buildDeviceIntelligenceResult: BuildDeviceIntelligenceResult) => void;
	onCollectionFail?: () => void;
	authToken?: string;
	apolloClient?: ApolloClient<NormalizedCacheObject>;
}

export type DeviceIntelligenceContextType = {
	updateJourney: (journey: Journey) => void;
	collectDeviceIntelligence: () => Promise<BuildDeviceIntelligenceResult | null>;
	reportFieldData: (fieldData: FieldData) => void;
};

export interface FieldState {
	[key: string]: FieldData;
}

export interface FieldData {
	name?: string;
	keystroke_count?: number;
	typing_speed_wpm?: number; // words per minute (float)
	first_keystroke_time?: number;
	last_keystroke_time?: number;
}

export const DeviceIntelligenceContext = createContext<DeviceIntelligenceContextType | null>(null);

const DEBOUNCE_MILLISECONDS = 300;

const debouncedBuildDeviceIntelligence = debounce(
	async (client, milliseconds_spent, registration_id, authToken, vendor_client_inputs, maxTypingSpeed) => {
		return buildDeviceIntelligence({
			authToken,
			client,
			input: {
				client_behavioral_data: {
					milliseconds_spent,
					call_status: PhoneCallStatus.Unknown,
					...(maxTypingSpeed !== undefined && { max_typing_speed: maxTypingSpeed }),
				},
				registration_id,
				vendor_client_inputs,
			},
		});
	},
	DEBOUNCE_MILLISECONDS,
	{ leading: true },
);

export const DeviceIntelligenceProvider: FC<DeviceIntelligenceProviderProps> = props => {
	const stopwatch = useStopwatch();
	const [registrationId, setRegistrationId] = useState<string | undefined>();
	const [deviceIntelligenceSessionKey, setDeviceIntelligenceSessionKey] = useState<string>(hyperid().uuid);
	const [journey, setJourney] = useState<Journey>(props.journey);
	const [vendors, setVendors] = useState<ReadonlyArray<DeviceVendor>>([]);
	const [vendorClientInputs, setVendorClientInputs] = useState<VendorClientInput[]>([]);
	const [fieldState, setFieldState] = useState<FieldState>({});
	const authToken = determineAuthToken(props);
	const client = determineApolloClient(props, authToken);
	const propsOnRegistrationComplete = props.onRegistrationComplete;
	const onCollectionFail = props.onCollectionFail;

	// Calculate max typing speed from all fields that meet the minimum keystroke requirement
	const calculateMaxTypingSpeed = useCallback((fieldState: FieldState): number | undefined => {
		const validTypingSpeeds = Object.values(fieldState)
			.map(field => field.typing_speed_wpm)
			.filter((speed): speed is number => speed !== undefined);
		return validTypingSpeeds.length > 0 ? Math.max(...validTypingSpeeds) : undefined;
	}, []);

	const updateJourney = useCallback((newJourney: Journey) => {
		setJourney(currentJourney => {
			// Update session key when transitioning to Login journey
			if (newJourney === Journey.Login && currentJourney !== Journey.Login) {
				// Schedule session key update - React will batch this with the journey update
				setDeviceIntelligenceSessionKey(hyperid().uuid);
			}
			return newJourney;
		});
		setVendors([]);
		setVendorClientInputs([]);
		setRegistrationId(undefined);
	}, []);

	const collectDeviceIntelligence = useCallback(async () => {
		if (!registrationId) {
			return Promise.reject(new Error('Registration ID is not available'));
		}

		const timer = stopwatch.getElapsedRunningTime();
		const maxTypingSpeed = calculateMaxTypingSpeed(fieldState);

		return await debouncedBuildDeviceIntelligence(
			client,
			timer,
			registrationId,
			authToken,
			vendorClientInputs,
			maxTypingSpeed,
		).catch(() => {
			onCollectionFail?.();
			return null;
		});
	}, [
		registrationId,
		stopwatch,
		calculateMaxTypingSpeed,
		fieldState,
		client,
		authToken,
		vendorClientInputs,
		onCollectionFail,
	]);

	const reportFieldData = useCallback((fieldData: FieldData) => {
		setFieldState(prevValue => {
			const newValue = { ...prevValue };
			if (fieldData.name) {
				newValue[fieldData.name] = fieldData;
			}
			return newValue;
		});
	}, []);

	const onRegistrationComplete = useCallback(
		(registrationId: string, vendors: DeviceVendor[]) => {
			setRegistrationId(registrationId);

			if (propsOnRegistrationComplete) {
				propsOnRegistrationComplete(registrationId, vendors);
			}
		},
		[propsOnRegistrationComplete],
	);

	const onVendorsComplete = useCallback((_registrationId: string, venInputs: VendorClientInput[]) => {
		setVendorClientInputs(venInputs);
	}, []);

	useEffect(() => {
		stopwatch.start();
	}, [stopwatch]);

	const providerValue = {
		vendors,
		updateJourney,
		collectDeviceIntelligence,
		reportFieldData,
	};

	return (
		<DeviceIntelligenceContext.Provider value={providerValue}>
			<AbstractDeviceRegistration
				authToken={authToken}
				apolloClient={client}
				journey={journey}
				deviceIntelligenceSessionKey={deviceIntelligenceSessionKey}
				onRegistrationComplete={onRegistrationComplete}
				onRegistrationFail={() => {
					onCollectionFail?.();
				}}
				onVendorsComplete={onVendorsComplete}
			/>
			{props.children}
		</DeviceIntelligenceContext.Provider>
	);
};
